import { isClient } from '../browser'
import { rAF } from '../raf'
import { isFullInContainer } from './position'
import { getStyle } from './style'

export const isScroll = (el: HTMLElement, isVertical?: boolean): boolean => {
  if (!isClient) return false

  const key = (
    {
      undefined: 'overflow',
      true: 'overflow-y',
      false: 'overflow-x',
    } as const
  )[String(isVertical)]!
  const overflow = getStyle(el, key)
  return ['scroll', 'auto', 'overlay'].some((s) => overflow.includes(s))
}

export const getScrollContainer = (
  el: HTMLElement,
  isVertical?: boolean
): Window | HTMLElement | undefined => {
  if (!isClient) return

  let parent: HTMLElement = el
  while (parent) {
    if ([window, document, document.documentElement].includes(parent))
      return window

    if (isScroll(parent, isVertical)) return parent

    parent = parent.parentNode as HTMLElement
  }

  return parent
}

export const getScrollLength = (
  el: HTMLElement,
  isVertical?: boolean
): number => {
  if (!isClient) return 0

  const scrollContainer = getScrollContainer(el, isVertical)
  if (!scrollContainer) return 0

  const clientProp = isVertical ? 'clientHeight' : 'clientWidth'
  const scrollProp = isVertical ? 'scrollHeight' : 'scrollWidth'
  if (
    [window, document, document.documentElement].includes(
      scrollContainer as HTMLElement
    )
  )
    return (
      document.documentElement[scrollProp] -
      document.documentElement[clientProp]
    )
  return (
    (scrollContainer as HTMLElement)[scrollProp] -
    (scrollContainer as HTMLElement)[clientProp]
  )
}

export const isInView = (el: HTMLElement): boolean => {
  let flag = true

  let parent: HTMLElement = el
  let lastParent: HTMLElement = el
  while (parent && flag) {
    parent = parent.parentNode as HTMLElement
    if (parent) {
      flag = isFullInContainer(lastParent, parent)
    }
    lastParent = parent
  }

  return flag
}

let scrollBarWidth: number
export const getScrollBarWidth = (namespace: string): number => {
  if (!isClient) return 0
  if (scrollBarWidth !== undefined) return scrollBarWidth

  const outer = document.createElement('div')
  outer.className = `${namespace}-scrollbar__wrap`
  outer.style.visibility = 'hidden'
  outer.style.width = '100px'
  outer.style.position = 'absolute'
  outer.style.top = '-9999px'
  document.body.appendChild(outer)

  const widthNoScroll = outer.offsetWidth
  outer.style.overflow = 'scroll'

  const inner = document.createElement('div')
  inner.style.width = '100%'
  outer.appendChild(inner)

  const widthWithScroll = inner.offsetWidth
  outer.parentNode?.removeChild(outer)
  scrollBarWidth = widthNoScroll - widthWithScroll

  return scrollBarWidth
}

/**
 * Scroll with in the container element, positioning the **selected** element at the top
 * of the container
 */
export function scrollIntoView(
  container: HTMLElement,
  selected: HTMLElement
): void {
  if (!isClient) return

  if (!selected) {
    container.scrollTop = 0
    return
  }

  const offsetParents: HTMLElement[] = []
  let pointer = selected.offsetParent
  while (
    pointer !== null &&
    container !== pointer &&
    container.contains(pointer)
  ) {
    offsetParents.push(pointer as HTMLElement)
    pointer = (pointer as HTMLElement).offsetParent
  }
  const top =
    selected.offsetTop +
    offsetParents.reduce((prev, curr) => prev + curr.offsetTop, 0)
  const bottom = top + selected.offsetHeight
  const viewRectTop = container.scrollTop
  const viewRectBottom = viewRectTop + container.clientHeight

  if (top < viewRectTop) {
    container.scrollTop = top
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight
  }
}

export function getScrollDistance(
  target: Window | HTMLElement,
  top: boolean
): number {
  if (!target) {
    return 0
  }

  const prop = top ? 'pageYOffset' : 'pageXOffset'
  const method = top ? 'scrollTop' : 'scrollLeft'
  const isWindow = target === window

  let distance = isWindow ? target[prop] : (target as HTMLElement)[method]
  // ie6,7,8 standard mode
  if (isWindow && typeof distance !== 'number') {
    distance = window.document.documentElement[method]
  }

  return distance
}

export function easeInOutCubic(
  t: number,
  b: number,
  c: number,
  d: number
): number {
  const cc = c - b
  t /= d / 2
  if (t < 1) {
    return (cc / 2) * t * t * t + b
  }
  return (cc / 2) * ((t -= 2) * t * t + 2) + b
}

export interface ScrollToOptions {
  /** Scroll container, default as window */
  getContainer?: () => HTMLElement | Window
  /** Scroll end callback */
  callback?: () => any
  /** Animation duration, default as 450 */
  duration?: number
}

export function scrollTo(y: number, options: ScrollToOptions = {}): void {
  const { getContainer = () => window, callback, duration = 450 } = options

  const container: Window | HTMLElement = getContainer()
  const scrollTop = getScrollDistance(container, true)
  const startTime = Date.now()

  const frameFunc = () => {
    const timestamp = Date.now()
    const time = timestamp - startTime
    const nextScrollTop = easeInOutCubic(
      time > duration ? duration : time,
      scrollTop,
      y,
      duration
    )
    if (container === window) {
      window.scrollTo(window.pageXOffset, nextScrollTop)
    } else {
      ;(container as HTMLElement).scrollTop = nextScrollTop
    }
    if (time < duration) {
      rAF(frameFunc)
    } else if (typeof callback === 'function') {
      callback()
    }
  }
  rAF(frameFunc)
}
